import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// firebase
import {
  addPSACard,
  listenToPSACards,
  updatePSACard,
  deletePSACard,
  createPSASubmission,
  listenToPSASubmissions,
  updatePSASubmission,
  deletePSASubmission,
  addCardToSubmission,
  assignCardToSubmission
} from '../../utils/firebase/psa';

// redux
import { selectUser } from '../../store/user/user.selector';
import { setSystemNotification } from '../../store/system/system.action';

// styles
import {
  PSAPageContainer,
  BtnContainer
} from './PSAPage.styles';

// components
import HeaderWidget from '../../components/widgets/header-widget/HeaderWidget';
import NewGradingModal from './components/new-grading-modal/NewGradingModal';
import NewSubmissionModal from './components/new-submission-modal/NewSubmissionModal';
import PSACardSelectionModal from './components/psa-card-selection-modal/PSACardSelectionModal';
import PSACardsDisplay from './components/psa-cards-display/PSACardsDisplay';
import PrimaryBtn from '../../components/btns/primary-btn/PrimaryBtn';
import Title from '../../components/widgets/components/title/Title';

export default function PSAPage() {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmissionModalOpen, setIsSubmissionModalOpen] = useState(false);
  const [isCardSelectionModalOpen, setIsCardSelectionModalOpen] = useState(false);
  const [gradingData, setGradingData] = useState(null);
  const [targetSubmissionID, setTargetSubmissionID] = useState(null); // For adding cards to specific submission
  const [isSaving, setIsSaving] = useState(false);
  const [psaCards, setPsaCards] = useState({});
  const [psaSubmissions, setPsaSubmissions] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const handleOpenNewGradingModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseNewGradingModal = () => {
    setIsModalOpen(false);
  };

  const handleChooseCard = async (data) => {
    if (!user?.uid) {
      dispatch(setSystemNotification('Error: User not authenticated'));
      return;
    }

    if (!data.card) {
      dispatch(setSystemNotification('Error: No card selected'));
      return;
    }

    setIsSaving(true);
    try {
      const gradingData = {
        type: data.type,
        grade: data.grade
      };

      await addPSACard(user.uid, data.card, gradingData);
      dispatch(setSystemNotification('Card successfully added to PSA tracking!'));

      // Close modal
      handleCloseNewGradingModal();
    } catch (error) {
      console.error('Error adding PSA card:', error);
      handleCloseNewGradingModal();
      setTimeout(() => {
        dispatch(setSystemNotification('Error adding card. Please try again.'));
      }, 100);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCardSelected = async (selectionData) => {
    // selectionData contains: { card, gradingData }
    if (!user?.uid) {
      dispatch(setSystemNotification('Error: User not authenticated'));
      return;
    }

    setIsSaving(true);
    try {
      // If targetSubmissionID is set, add card to that submission
      if (targetSubmissionID) {
        await addCardToSubmission(user.uid, selectionData.card, selectionData.gradingData, targetSubmissionID);
        setTargetSubmissionID(null);
        dispatch(setSystemNotification('Card successfully added to submission!'));
      } else {
        await addPSACard(user.uid, selectionData.card, selectionData.gradingData);
        dispatch(setSystemNotification('Card successfully added to PSA tracking!'));
      }

      // Close modals first so notification is visible
      setIsCardSelectionModalOpen(false);
      handleCloseNewGradingModal();
      setGradingData(null);
    } catch (error) {
      console.error('Error adding PSA card:', error);
      // Close modals even on error
      setIsCardSelectionModalOpen(false);
      handleCloseNewGradingModal();
      setGradingData(null);
      setTargetSubmissionID(null);
      // Show error notification after modals are closed
      setTimeout(() => {
        dispatch(setSystemNotification('Error adding card. Please try again.'));
      }, 100);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseCardSelection = () => {
    setIsCardSelectionModalOpen(false);
    setGradingData(null);
    setTargetSubmissionID(null);
  };

  const handleAddCardToSubmission = (submissionID) => {
    // Open card selection modal for this submission
    setTargetSubmissionID(submissionID);
    setGradingData({ type: 'new', service: null }); // Default to new card
    setIsCardSelectionModalOpen(true);
  };

  const handleAssignCardToSubmission = async (cardID, submissionID) => {
    if (!user?.uid) {
      dispatch(setSystemNotification('Error: User not authenticated'));
      return;
    }

    try {
      await assignCardToSubmission(user.uid, cardID, submissionID);
      dispatch(setSystemNotification('Card assigned to submission successfully!'));
    } catch (error) {
      console.error('Error assigning card to submission:', error);
      dispatch(setSystemNotification('Error assigning card to submission. Please try again.'));
    }
  };

  // Set up real-time listener for PSA cards
  useEffect(() => {
    if (!user?.uid) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const unsubscribe = listenToPSACards(user.uid, (cards) => {
      setPsaCards(cards);
      setIsLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [user?.uid]);

  // Set up real-time listener for PSA submissions
  useEffect(() => {
    if (!user?.uid) return;

    const unsubscribe = listenToPSASubmissions(user.uid, (submissions) => {
      setPsaSubmissions(submissions);
    });

    return () => {
      unsubscribe();
    };
  }, [user?.uid]);

  const handleUpdateCard = async (cardID, updates) => {
    if (!user?.uid) {
      dispatch(setSystemNotification('Error: User not authenticated'));
      return;
    }

    try {
      await updatePSACard(user.uid, cardID, updates);
      dispatch(setSystemNotification('PSA card updated successfully!'));
    } catch (error) {
      console.error('Error updating PSA card:', error);
      dispatch(setSystemNotification('Error updating PSA card. Please try again.'));
      throw error;
    }
  };

  const handleDeleteCard = async (cardID) => {
    if (!user?.uid) {
      dispatch(setSystemNotification('Error: User not authenticated'));
      return;
    }

    try {
      await deletePSACard(user.uid, cardID);
      dispatch(setSystemNotification('PSA card deleted successfully!'));
    } catch (error) {
      console.error('Error deleting PSA card:', error);
      dispatch(setSystemNotification('Error deleting PSA card. Please try again.'));
      throw error;
    }
  };

  const handleOpenSubmissionModal = () => {
    setIsSubmissionModalOpen(true);
  };

  const handleCloseSubmissionModal = () => {
    setIsSubmissionModalOpen(false);
  };

  const handleCreateSubmission = async (submissionData) => {
    if (!user?.uid) {
      dispatch(setSystemNotification('Error: User not authenticated'));
      return;
    }

    try {
      await createPSASubmission(user.uid, submissionData);
      setIsSubmissionModalOpen(false);
      setTimeout(() => {
        dispatch(setSystemNotification('Submission created successfully!'));
      }, 100);
    } catch (error) {
      console.error('Error creating submission:', error);
      setTimeout(() => {
        dispatch(setSystemNotification('Error creating submission. Please try again.'));
      }, 100);
    }
  };

  const handleUpdateSubmission = async (submissionID, updates) => {
    if (!user?.uid) {
      dispatch(setSystemNotification('Error: User not authenticated'));
      return;
    }

    try {
      await updatePSASubmission(user.uid, submissionID, updates);
      dispatch(setSystemNotification('Submission updated successfully!'));
    } catch (error) {
      console.error('Error updating submission:', error);
      dispatch(setSystemNotification('Error updating submission. Please try again.'));
      throw error;
    }
  };

  const handleDeleteSubmission = async (submissionID) => {
    if (!user?.uid) {
      dispatch(setSystemNotification('Error: User not authenticated'));
      return;
    }

    try {
      await deletePSASubmission(user.uid, submissionID);
      dispatch(setSystemNotification('Submission deleted successfully!'));
    } catch (error) {
      console.error('Error deleting submission:', error);
      dispatch(setSystemNotification('Error deleting submission. Please try again.'));
      throw error;
    }
  };

  return (
    <PSAPageContainer>
      <HeaderWidget fullWidth={true}>
        <Title text="PSA TRACKER" />
        <BtnContainer>
          <PrimaryBtn title="+ NEW GRADING" onClick={handleOpenNewGradingModal} />
          <PrimaryBtn title="+ NEW SUBMISSION" onClick={handleOpenSubmissionModal} />
        </BtnContainer>
      </HeaderWidget>
      <NewGradingModal
        isOpen={isModalOpen}
        onClose={handleCloseNewGradingModal}
        onChooseCard={handleChooseCard}
      />
      <NewSubmissionModal
        isOpen={isSubmissionModalOpen}
        onClose={handleCloseSubmissionModal}
        onCreateSubmission={handleCreateSubmission}
      />
      <PSACardSelectionModal
        isOpen={isCardSelectionModalOpen}
        onClose={handleCloseCardSelection}
        onCardSelect={handleCardSelected}
        gradingData={gradingData}
        isSaving={isSaving}
      />
      <PSACardsDisplay
        psaCards={psaCards}
        psaSubmissions={psaSubmissions}
        isLoading={isLoading}
        onUpdateCard={handleUpdateCard}
        onDeleteCard={handleDeleteCard}
        onUpdateSubmission={handleUpdateSubmission}
        onDeleteSubmission={handleDeleteSubmission}
        onAddCardToSubmission={handleAddCardToSubmission}
        onAssignCardToSubmission={handleAssignCardToSubmission}
      />
    </PSAPageContainer>
  )
}
